/**
 * Test-mode banner.
 *
 * PM 2026-06-10 (rotation fix): when test mode is active (the URL has
 * `?test=1` or the persistent localStorage flag is set) the platform
 * rotates puzzles every 10 minutes instead of every 24 hours. The banner
 * tells the tester WHY the puzzle just changed under them, and shows a
 * live countdown to the next rotation.
 *
 * Two visual states:
 *   - Expanded: a centred chip near the top of the page. Dim background,
 *     foreground text. Includes a small X to collapse it to the corner pill.
 *   - Collapsed: a tiny pill in the bottom-right corner. Click to expand
 *     back to the centred chip.
 *
 * Caller does NOT need to gate the mount on `isTestMode()` - the banner
 * is always mountable, and the `active` prop tells it whether to render
 * anything at all. The frontend boot sequence calls
 * `mountTestModeBanner(document.body, { active: isTestMode(), ... })`.
 *
 * Light + dark mode share styles via OKLCH tokens (see
 * `Vault/Design/Design Brief.md` for the rules).
 */

export interface TestModeBannerProps {
  /** When false the banner renders nothing. */
  active: boolean;
  /**
   * Epoch ms at which the current 10-minute bucket ends. The countdown
   * refreshes its label every second by comparing this to `Date.now()`.
   * Caller is responsible for recomputing this (via `nextBucketAt()`)
   * and calling `handle.update(...)` when the bucket flips.
   */
  nextRotationAtMs: number;
  /** Number of minutes per bucket. Display only - the count is `nextRotationAtMs`. */
  bucketMinutes?: number;
}

export interface TestModeBannerHandle {
  /** Swap props (typically just `nextRotationAtMs`). */
  update: (props: TestModeBannerProps) => void;
  /** Tear down DOM + intervals. */
  destroy: () => void;
}

interface InternalRefs {
  root: HTMLDivElement;
  chip: HTMLDivElement;
  chipLabel: HTMLSpanElement;
  chipCountdown: HTMLSpanElement;
  chipCollapse: HTMLButtonElement;
  pill: HTMLButtonElement;
  state: 'expanded' | 'collapsed';
  intervalId: ReturnType<typeof setInterval> | null;
}

const STORAGE_KEY = 'gives.testMode.banner';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'collapsed';
  } catch {
    return false;
  }
}

function writeCollapsed(collapsed: boolean): void {
  try {
    if (collapsed) localStorage.setItem(STORAGE_KEY, 'collapsed');
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // private mode - ignore.
  }
}

function formatMsRemaining(ms: number): string {
  if (ms <= 0) return '00:00';
  const totalSec = Math.floor(ms / 1000);
  const mm = Math.floor(totalSec / 60);
  const ss = totalSec % 60;
  const mmStr = String(mm).padStart(2, '0');
  const ssStr = String(ss).padStart(2, '0');
  return `${mmStr}:${ssStr}`;
}

export function mountTestModeBanner(
  host: HTMLElement,
  initial: TestModeBannerProps,
): TestModeBannerHandle {
  const root = document.createElement('div');
  root.className = 'gv-test-banner-root';
  root.dataset.active = initial.active ? '1' : '0';

  // ---- expanded chip ----
  const chip = document.createElement('div');
  chip.className = 'gv-test-banner-chip';
  chip.setAttribute('role', 'status');
  chip.setAttribute('aria-live', 'polite');

  const chipLabel = document.createElement('span');
  chipLabel.className = 'gv-test-banner-label';
  const bucketMin = initial.bucketMinutes ?? 10;
  chipLabel.textContent =
    `Test mode: puzzles rotate every ${bucketMin} minutes, next rotation in `;

  const chipCountdown = document.createElement('span');
  chipCountdown.className = 'gv-test-banner-countdown';
  chipCountdown.textContent = formatMsRemaining(
    initial.nextRotationAtMs - Date.now(),
  );

  const chipCollapse = document.createElement('button');
  chipCollapse.type = 'button';
  chipCollapse.className = 'gv-test-banner-collapse';
  chipCollapse.setAttribute('aria-label', 'Collapse test mode banner');
  chipCollapse.textContent = 'x';

  chip.append(chipLabel, chipCountdown, chipCollapse);

  // ---- collapsed pill ----
  const pill = document.createElement('button');
  pill.type = 'button';
  pill.className = 'gv-test-banner-pill';
  pill.setAttribute('aria-label', 'Show test mode banner');
  pill.textContent = 'Test mode';

  root.append(chip, pill);
  host.appendChild(root);

  const refs: InternalRefs = {
    root,
    chip,
    chipLabel,
    chipCountdown,
    chipCollapse,
    pill,
    state: readCollapsed() ? 'collapsed' : 'expanded',
    intervalId: null,
  };

  function applyState(): void {
    refs.root.dataset.state = refs.state;
  }

  function collapse(): void {
    refs.state = 'collapsed';
    writeCollapsed(true);
    applyState();
  }

  function expand(): void {
    refs.state = 'expanded';
    writeCollapsed(false);
    applyState();
  }

  chipCollapse.addEventListener('click', collapse);
  pill.addEventListener('click', expand);

  applyState();

  let nextRotationAt = initial.nextRotationAtMs;

  function tick(): void {
    refs.chipCountdown.textContent = formatMsRemaining(nextRotationAt - Date.now());
  }

  function startTicker(): void {
    if (refs.intervalId !== null) return;
    refs.intervalId = setInterval(tick, 1000);
  }

  function stopTicker(): void {
    if (refs.intervalId === null) return;
    clearInterval(refs.intervalId);
    refs.intervalId = null;
  }

  if (initial.active) {
    startTicker();
    tick();
  }

  return {
    update(props: TestModeBannerProps): void {
      refs.root.dataset.active = props.active ? '1' : '0';
      nextRotationAt = props.nextRotationAtMs;
      const nextBucketMin = props.bucketMinutes ?? 10;
      refs.chipLabel.textContent =
        `Test mode: puzzles rotate every ${nextBucketMin} minutes, next rotation in `;
      if (props.active) startTicker();
      else stopTicker();
      tick();
    },
    destroy(): void {
      stopTicker();
      refs.root.remove();
    },
  };
}
